import React, { useEffect, useState } from "react";
import { api, formatApiError, formatCurrency, formatDate } from "@/lib/api";
import { EmptyState, StatusBadge } from "@/components/app/ui-bits";
import { Briefcase, ArrowLeft } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const fmt = (v) => (v == null ? "—" : formatCurrency(v));
const pct = (v) => (v == null ? "—" : `${v}%`);

export default function ProjectAccountingTab() {
  const [pill, setPill] = useState("profitability");
  const [data, setData] = useState(null);
  const [wip, setWip] = useState(null);
  const [drill, setDrill] = useState(null); // project_id

  useEffect(() => {
    Promise.all([api.get("/finance/pa/overview"), api.get("/finance/pa/wip")])
      .then(([o, w]) => { setData(o.data); setWip(w.data); })
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  if (drill) return <ProjectDrill projectId={drill} onBack={() => setDrill(null)} />;

  return (
    <div className="space-y-4" data-testid="project-accounting-tab">
      <div className="flex flex-wrap gap-1.5">
        {[["profitability", "Profitability"], ["wip", "WIP / Billing"]].map(([v, l]) => (
          <button key={v} onClick={() => setPill(v)} data-testid={`pa-pill-${v}`}
            className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${pill === v ? "bg-[#1E5CBA] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
            {l}
          </button>
        ))}
      </div>

      {data && (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
          <Kpi label="Projects w/ GL activity" value={data.summary.projects} raw />
          <Kpi label="Project revenue" value={data.summary.revenue} />
          <Kpi label="Project cost" value={data.summary.total_cost} />
          <Kpi label="Net result" value={data.summary.net} tone={data.summary.net >= 0 ? "emerald" : "rose"} />
          <Kpi label="Unbilled WIP" value={data.summary.unbilled_wip} tone="amber" />
        </div>
      )}

      {pill === "profitability" && (
        <div className="dc-card overflow-hidden">
          <div className="border-b border-slate-100 px-5 py-3">
            <h3 className="font-display text-[15px] font-semibold">Project profitability (GL-driven)</h3>
            <div className="text-[11px] text-slate-400">Revenue, cost and margin aggregated from journal lines carrying a project dimension. Click a row to drill into the project P&L and ledger.</div>
          </div>
          {!data || data.items.length === 0 ? (
            <EmptyState icon={Briefcase} title="No project-tagged GL activity" description="Journals, invoices and bills tagged with a project will roll up here." />
          ) : (
            <div className="responsive-table-wrap">
              <table className="data-table w-full min-w-[1150px]">
                <thead>
                  <tr>
                    <th>Project</th><th>Status</th>
                    <th className="text-right">Revenue</th><th className="text-right">Direct cost</th><th className="text-right">Overhead</th>
                    <th className="text-right">Net</th><th className="text-right">Margin</th>
                    <th className="text-right">Budget</th><th className="text-right">Budget used</th>
                    <th className="text-right">Billed</th><th className="text-right">WIP</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((r) => (
                    <tr key={r.project_id} className="text-sm cursor-pointer hover:bg-slate-50/70" onClick={() => setDrill(r.project_id)} data-testid={`pa-row-${r.project_id}`}>
                      <td className="px-5 py-3 font-semibold text-slate-900">{r.name}{r.code && <span className="ml-1 font-mono-alt text-[10px] text-slate-400">{r.code}</span>}{r.location && <div className="text-[10px] font-normal text-teal-700">📍 {r.location}</div>}</td>
                      <td className="px-5 py-3">{r.status ? <StatusBadge status={r.status} /> : "—"}</td>
                      <td className="px-5 py-3 text-right font-mono-alt tabular-nums">{fmt(r.revenue)}</td>
                      <td className="px-5 py-3 text-right font-mono-alt tabular-nums">{fmt(r.direct_cost)}</td>
                      <td className="px-5 py-3 text-right font-mono-alt tabular-nums">{fmt(r.overhead)}</td>
                      <td className={`px-5 py-3 text-right font-mono-alt tabular-nums font-semibold ${r.net >= 0 ? "text-emerald-700" : "text-rose-600"}`}>{fmt(r.net)}</td>
                      <td className="px-5 py-3 text-right font-mono-alt tabular-nums">{pct(r.margin_pct)}</td>
                      <td className="px-5 py-3 text-right font-mono-alt tabular-nums">{r.budget ? fmt(r.budget) : "—"}</td>
                      <td className={`px-5 py-3 text-right font-mono-alt tabular-nums ${r.budget_utilization_pct > 100 ? "font-bold text-rose-600" : ""}`}>{pct(r.budget_utilization_pct)}</td>
                      <td className="px-5 py-3 text-right font-mono-alt tabular-nums">{fmt(r.billed)}</td>
                      <td className="px-5 py-3 text-right font-mono-alt tabular-nums text-amber-700">{fmt(r.unbilled_wip)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {pill === "wip" && (
        <div className="dc-card overflow-hidden">
          <div className="border-b border-slate-100 px-5 py-3">
            <h3 className="font-display text-[15px] font-semibold">Work-in-progress · cost vs billing</h3>
            <div className="text-[11px] text-slate-400">Unbilled WIP = cost incurred not yet invoiced · Overbilled = invoiced ahead of cost.</div>
          </div>
          {!wip || wip.items.length === 0 ? (
            <EmptyState icon={Briefcase} title="No WIP" description="Project costs and invoices will produce a WIP schedule here." />
          ) : (
            <table className="data-table w-full">
              <thead><tr><th>Project</th><th>Status</th><th className="text-right">Cost to date</th><th className="text-right">Billed to date</th><th className="text-right">% billed</th><th className="text-right">Unbilled WIP</th><th className="text-right">Overbilled</th></tr></thead>
              <tbody>
                {wip.items.map((r) => (
                  <tr key={r.project_id} className="text-sm" data-testid={`pa-wip-${r.project_id}`}>
                    <td className="px-5 py-3 font-semibold">{r.name}</td>
                    <td className="px-5 py-3">{r.status ? <StatusBadge status={r.status} /> : "—"}</td>
                    <td className="px-5 py-3 text-right font-mono-alt tabular-nums">{fmt(r.cost_to_date)}</td>
                    <td className="px-5 py-3 text-right font-mono-alt tabular-nums">{fmt(r.billed_to_date)}</td>
                    <td className="px-5 py-3 text-right font-mono-alt tabular-nums">{pct(r.pct_billed)}</td>
                    <td className="px-5 py-3 text-right font-mono-alt tabular-nums font-semibold text-amber-700">{fmt(r.unbilled_wip)}</td>
                    <td className="px-5 py-3 text-right font-mono-alt tabular-nums">{fmt(r.overbilled)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="text-sm font-semibold bg-slate-50/60">
                  <td className="px-5 py-2.5" colSpan={5}>Totals</td>
                  <td className="px-5 py-2.5 text-right font-mono-alt tabular-nums text-amber-700">{fmt(wip.total_unbilled_wip)}</td>
                  <td className="px-5 py-2.5 text-right font-mono-alt tabular-nums">{fmt(wip.total_overbilled)}</td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, tone, raw }) {
  const color = tone === "emerald" ? "text-emerald-700" : tone === "rose" ? "text-rose-600" : tone === "amber" ? "text-amber-700" : "text-slate-900";
  return (
    <div className="dc-card p-4">
      <div className="overline">{label}</div>
      <div className={`mt-1 font-display text-xl font-bold tabular-nums ${color}`}>{raw ? value : fmt(value)}</div>
    </div>
  );
}

function ProjectDrill({ projectId, onBack }) {
  const [pnl, setPnl] = useState(null);
  const [ledger, setLedger] = useState([]);
  useEffect(() => {
    Promise.all([
      api.get(`/finance/pa/${projectId}/pnl`),
      api.get(`/finance/pa/${projectId}/ledger`, { params: { limit: 100 } }),
    ]).then(([p, l]) => { setPnl(p.data); setLedger(l.data.items || []); })
      .catch((e) => toast.error(formatApiError(e)));
  }, [projectId]);

  if (!pnl) return <div className="text-sm text-slate-500">Loading…</div>;
  const Section = ({ title, rows }) => rows.length > 0 && (
    <>
      <tr className="bg-slate-50/70"><td colSpan={2} className="px-5 py-1.5 overline">{title}</td></tr>
      {rows.map((a) => (
        <tr key={a.account_code} className="text-sm">
          <td className="px-5 py-2"><span className="font-mono-alt text-xs text-slate-400 mr-2">{a.account_code}</span>{a.account_name}</td>
          <td className="px-5 py-2 text-right font-mono-alt tabular-nums">{fmt(a.amount)}</td>
        </tr>
      ))}
    </>
  );

  return (
    <div className="space-y-4" data-testid="pa-drill">
      <div className="flex items-center gap-3">
        <Button variant="outline" size="sm" className="h-8" onClick={onBack} data-testid="pa-drill-back"><ArrowLeft size={13} className="mr-1" /> All projects</Button>
        <h3 className="font-display text-lg font-semibold">{pnl.project.name} — Project P&L</h3>
      </div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Kpi label="Revenue" value={pnl.total_revenue} />
        <Kpi label="Direct cost" value={pnl.total_direct_cost} />
        <Kpi label="Gross margin" value={pnl.gross_margin} tone={pnl.gross_margin >= 0 ? "emerald" : "rose"} />
        <Kpi label="Net" value={pnl.net} tone={pnl.net >= 0 ? "emerald" : "rose"} />
        <Kpi label="Budget used" value={pnl.budget_utilization_pct == null ? "—" : `${pnl.budget_utilization_pct}%`} raw />
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="dc-card overflow-hidden">
          <div className="border-b border-slate-100 px-5 py-3"><h3 className="font-display text-[15px] font-semibold">P&L by account</h3></div>
          <table className="data-table w-full">
            <tbody>
              <Section title="Revenue" rows={pnl.revenue} />
              <Section title="Direct costs" rows={pnl.direct_cost} />
              <Section title="Overhead" rows={pnl.overhead} />
              <tr className="text-sm font-bold bg-slate-50/60"><td className="px-5 py-2.5">Net result</td><td className={`px-5 py-2.5 text-right font-mono-alt tabular-nums ${pnl.net >= 0 ? "text-emerald-700" : "text-rose-600"}`}>{fmt(pnl.net)}</td></tr>
            </tbody>
          </table>
        </div>
        <div className="dc-card overflow-hidden">
          <div className="border-b border-slate-100 px-5 py-3"><h3 className="font-display text-[15px] font-semibold">Project ledger (latest {ledger.length})</h3></div>
          <div className="max-h-[480px] overflow-y-auto">
            <table className="data-table w-full">
              <thead><tr><th>Date</th><th>Entry</th><th>Account</th><th className="text-right">Debit</th><th className="text-right">Credit</th></tr></thead>
              <tbody>
                {ledger.map((l) => (
                  <tr key={l.id} className="text-xs">
                    <td className="px-4 py-1.5 font-mono-alt tabular-nums">{formatDate(l.date)}</td>
                    <td className="px-4 py-1.5 font-mono-alt">{l.entry_no}</td>
                    <td className="px-4 py-1.5"><span className="font-mono-alt text-slate-400 mr-1">{l.account_code}</span>{l.account_name}</td>
                    <td className="px-4 py-1.5 text-right font-mono-alt tabular-nums">{l.debit ? fmt(l.debit) : ""}</td>
                    <td className="px-4 py-1.5 text-right font-mono-alt tabular-nums">{l.credit ? fmt(l.credit) : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
